import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BarcodeScanner from '../components/BarcodeScanner';
import { useBranch } from '../contexts/BranchContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

export default function StockManagement({ apiUrl }) {
  const navigate = useNavigate();
  const { effectiveBranchId, isAllBranches } = useBranch();
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  // ต้นทุน/กำไรเป็นข้อมูลของเจ้าของร้านเท่านั้น — ผู้จัดการกรอกต้นทุนตอนซื้อของเข้าได้
  // ตามปกติ (จำเป็นต่องาน) แต่ไม่ควรเห็นต้นทุนเฉลี่ย/กำไรต่อจานที่คำนวณย้อนออกมา
  const isBranchManager = localStorage.getItem('adminRole') === 'manager';
  // จัดการสต็อกเขียนข้อมูลจริงของสาขาเดียวเสมอ ห้ามเผลอทำตอนเลือก "รวมทุกสาขา" เพราะ
  // effectiveBranchId จะ fallback ไปสาขาแรกตามลำดับตัวอักษรแทน ไม่ใช่สาขาที่ตั้งใจ
  const blocked = isAllBranches || !effectiveBranchId;
  const [stockItems, setStockItems] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | low | tracked
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOpeningModal, setShowOpeningModal] = useState(false);
  const [openingSubmitting, setOpeningSubmitting] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadData();
  }, [blocked]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      const [stockRes, menuRes, catRes] = await Promise.all([
        // includeUntracked: menu items with no stockItem row yet are otherwise
        // invisible here, so there is no way to start tracking them.
        fetch(`${apiUrl}/api/stock?includeUntracked=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${apiUrl}/menu`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${apiUrl}/categories`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const stockData = await stockRes.json();
      const menuData = await menuRes.json();
      const catData = await catRes.json();

      setStockItems(stockData.data || []);
      setMenuItems(menuData.data || menuData);
      setCategories(catData.data || catData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);

  const handlePurchase = async (formData) => {
    if (purchaseSubmitting) return;
    setPurchaseSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/stock/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'บันทึกการซื้อเข้าสำเร็จ' });
        setShowPurchaseModal(false);
        loadData();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: `เกิดข้อผิดพลาด: ${error.message}` });
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  const handleUpdateSettings = async (menuItemId, settings) => {
    if (settingsSubmitting) return;
    setSettingsSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/stock/${menuItemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'อัพเดทการตั้งค่าสำเร็จ' });
        setShowEditModal(false);
        loadData();
      } else {
        // Previously silent — a failed save looked identical to a successful one
        const error = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: `บันทึกไม่สำเร็จ: ${error.message || 'ไม่ทราบสาเหตุ'}` });
      }
    } catch (error) {
      console.error('Update failed:', error);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
    } finally {
      setSettingsSubmitting(false);
    }
  };

  const handleOpeningBalance = async (menuItemId, payload) => {
    if (openingSubmitting) return;
    setOpeningSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/stock/${menuItemId}/opening-balance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'ตั้งยอดยกมาสำเร็จ' });
        setShowOpeningModal(false);
        loadData();
      } else {
        const error = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: `เกิดข้อผิดพลาด: ${error.message || 'ไม่ทราบสาเหตุ'}` });
      }
    } catch (error) {
      console.error('Opening balance failed:', error);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
    } finally {
      setOpeningSubmitting(false);
    }
  };

  const handleIssue = async (payload) => {
    if (issueSubmitting) return;
    setIssueSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/stock/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (response.ok) {
        const shortfall = result.data?.shortfall || 0;
        setMessage(shortfall > 0
          ? { type: 'error', text: `บันทึกแล้ว แต่ของไม่พอ ${shortfall} — หักได้เท่าที่มี` }
          : { type: 'success', text: 'บันทึกการเบิกใช้สำเร็จ' });
        setShowIssueModal(false);
        loadData();
      } else {
        setMessage({ type: 'error', text: `เกิดข้อผิดพลาด: ${result.message || 'ไม่ทราบสาเหตุ'}` });
      }
    } catch (error) {
      console.error('Issue failed:', error);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
    } finally {
      setIssueSubmitting(false);
    }
  };

  // เข้มกว่าสีชุดเดิมของระบบ เพราะตารางสต็อกพื้นขาว ส่วนสีเดิมถูกเลือกมา
  // สำหรับพื้นเข้ม ใช้ตรงนี้แล้วอ่านไม่ออกโดยเฉพาะสีส้มกับเขียว
  const getStockColor = (item) => {
    if (item.currentQuantity <= 0) return '#c0392b';
    if (item.isLowStock) return '#b26a00';
    return '#1e7e4a';
  };

  const categoryName = (id) => {
    const cat = categories.find(c => (c._id || c.id) === id);
    return cat ? cat.name : '-';
  };

  const filteredItems = stockItems.filter(item => {
    if (filter === 'low' && !item.isLowStock) return false;
    if (filter === 'tracked' && !item.tracked) return false;
    if (categoryFilter && item.menuItem?.categoryId !== categoryFilter) return false;
    if (searchTerm && !item.menuItem?.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  // นับจำนวนต่อหมวดไว้โชว์บนปุ่ม จะได้รู้ว่าหมวดไหนมีของกี่ตัวโดยไม่ต้องกดเข้าไปดู
  const countByCategory = stockItems.reduce((acc, item) => {
    const id = item.menuItem?.categoryId;
    if (id) acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#fff3cd', color: '#856404', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — จัดการสต็อกต้องระบุสาขาเดียวเสมอ
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการทำรายการผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div>
      <Toast message={message} onDismiss={() => setMessage(null)} />
      <div className="page-header">
        <h2>📦 จัดการสต็อก</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowPurchaseModal(true)} className="button">
            ➕ ซื้อสินค้าเข้า
          </button>
          <button
            onClick={() => setShowIssueModal(true)}
            className="button"
            style={{ background: 'linear-gradient(135deg, #8e44ad 0%, #71368a 100%)' }}
          >
            📤 เบิกใช้
          </button>
          <button
            onClick={() => navigate('/stock/count')}
            className="button"
            style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
          >
            📋 เดินสต็อก
          </button>
          <button
            onClick={() => navigate('/stock/history')}
            className="button"
            style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
          >
            📊 ประวัติการเคลื่อนไหว
          </button>
          {!isBranchManager && (
            <button
              onClick={() => navigate('/stock/report')}
              className="button"
              style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
            >
              💰 รายงานต้นทุน
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="🔍 ค้นหาเมนู..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '0.5rem' }}
          />
          <button
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'button' : 'button-secondary'}
          >
            ทั้งหมด
          </button>
          <button
            onClick={() => setFilter('tracked')}
            className={filter === 'tracked' ? 'button' : 'button-secondary'}
            style={filter === 'tracked' ? { background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)' } : {}}
          >
            ✅ เปิดใช้แล้ว
          </button>
          <button
            onClick={() => setFilter('low')}
            className={filter === 'low' ? 'button' : 'button-secondary'}
            style={filter === 'low' ? { background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' } : {}}
          >
            ⚠️ สต๊อกต่ำ
          </button>
        </div>

        {/* กรองตามหมวด — 53 เมนูในหน้าเดียวหาอะไรไม่เจอ */}
        <div style={{
          display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
          marginTop: '1rem', paddingTop: '1rem',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <button
            onClick={() => setCategoryFilter('')}
            className={categoryFilter === '' ? 'button' : 'button-secondary'}
            style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
          >
            ทุกหมวด ({stockItems.length})
          </button>
          {categories.map(cat => {
            const id = cat._id || cat.id;
            const count = countByCategory[id] || 0;
            if (count === 0) return null;
            return (
              <button
                key={id}
                onClick={() => setCategoryFilter(categoryFilter === id ? '' : id)}
                className={categoryFilter === id ? 'button' : 'button-secondary'}
                style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
              >
                {cat.name} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Stock Table */}
      <div className="card">
        <div style={{ overflowX: 'auto' }}>
          <table className="table-nowrap">
            <thead>
              {/* สถานะ ย้ายมาเป็นคอลัมน์ที่ 2 (ต่อจากเมนู) — ตารางมี 9-10 คอลัมน์ ต้องเลื่อนขวามากถึงจะเห็น
                  เดิมสถานะอยู่คอลัมน์ที่ 9 — พนักงานต้องเลื่อนทุกครั้งจึงจะเห็นว่าโต๊ะไหนหมด/ต่ำกว่า
                  ย้ายมาไว้ต้นตาราง เห็นได้ทันทีโดยไม่ต้องเลื่อน */}
              <tr>
                <th>เมนู</th>
                <th>สถานะ</th>
                <th>หมวด</th>
                <th>วิธีนับ</th>
                <th>สต๊อกคงเหลือ</th>
                <th>ขั้นต่ำ</th>
                {!isBranchManager && <th>ต้นทุนเฉลี่ย</th>}
                <th>ราคาขาย</th>
                {!isBranchManager && <th>กำไร/จาน</th>}
                <th>แอคชั่น</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                const quantityPerServing = item.menuItem?.quantityPerServing || 1;
                const costPerServing = item.averageCost * quantityPerServing;
                const profit = (item.menuItem?.price || 0) - costPerServing;
                const margin = item.menuItem?.price > 0
                  ? (profit / item.menuItem.price * 100).toFixed(1)
                  : 0;

                // ตารางนี้พื้นขาว — ใช้แถบสีอ่อนบอกว่ายังไม่เปิดใช้สต็อก
                // แทนการหรี่ opacity ซึ่งทำให้ตัวหนังสืออ่านไม่ออก
                return (
                  <tr key={item.menuItemId} style={item.tracked ? undefined : { background: '#f4f6f8' }}>
                    <td style={{ fontWeight: '600', color: item.tracked ? undefined : '#4b5563' }}>
                      {item.menuItem?.name || '-'}
                    </td>
                    <td>
                      {!item.tracked && (
                        <span style={{ color: '#6b7280', fontWeight: '600' }}>⚪ ปิดอยู่</span>
                      )}
                      {item.tracked && item.currentQuantity <= 0 && (
                        <span style={{ color: '#c0392b', fontWeight: '700' }}>🔴 หมด</span>
                      )}
                      {item.tracked && item.currentQuantity > 0 && item.isLowStock && (
                        <span style={{ color: '#b26a00', fontWeight: '700' }}>⚠️ ต่ำ</span>
                      )}
                      {item.tracked && item.currentQuantity > 0 && !item.isLowStock && (
                        <span style={{ color: '#1e7e4a', fontWeight: '700' }}>✅ ปกติ</span>
                      )}
                    </td>
                    <td style={{ color: '#5a6472', fontSize: '0.88rem', fontWeight: 500 }}>
                      {categoryName(item.menuItem?.categoryId)}
                    </td>
                    <td>
                      {!item.tracked ? '-' : item.trackingMode === 'periodic' ? (
                        <span style={{ color: '#7d3c98', fontWeight: 700, fontSize: '0.85rem' }}
                              title="ลูกค้าหยิบเองจากตู้ — รู้ยอดจากการเดินสต็อก">
                          🧊 ตู้บริการตัวเอง
                        </span>
                      ) : (
                        <span style={{ color: '#1f6fb2', fontWeight: 700, fontSize: '0.85rem' }}
                              title="ลูกค้าสั่งผ่าน QR — ระบบหักให้ทุกออเดอร์">
                          📱 สั่งผ่าน QR
                        </span>
                      )}
                    </td>
                    <td>
                      {item.tracked ? (
                        <span style={{ color: getStockColor(item), fontWeight: '700' }}>
                          {item.currentQuantity.toFixed(2)} {item.unit}
                        </span>
                      ) : (
                        <span style={{ color: '#6b7280', fontWeight: 500 }}>ยังไม่ได้เปิดสต็อกให้เมนูนี้</span>
                      )}
                    </td>
                    <td>
                      {item.minQuantity === null
                        ? <span style={{ color: '#b26a00', fontWeight: 600 }}>ต้องตั้งค่า</span>
                        : `${item.minQuantity} ${item.unit || ''}`}
                    </td>
                    {!isBranchManager && (
                      <td>{item.tracked ? `${item.averageCost.toFixed(2)} ฿/${item.unit}` : '-'}</td>
                    )}
                    <td>{item.menuItem?.price?.toFixed(2) || '-'} ฿</td>
                    {!isBranchManager && (
                      <td>
                        {item.tracked ? (
                          <span style={{ color: profit >= 0 ? '#1e7e4a' : '#c0392b', fontWeight: 600 }}>
                            {profit.toFixed(2)} ฿ ({margin}%)
                          </span>
                        ) : '-'}
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            setSelectedItem(item);
                            setShowOpeningModal(true);
                          }}
                          className="button"
                          style={{
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.85rem',
                            background: item.tracked
                              ? 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)'
                              : 'linear-gradient(135deg, #27ae60 0%, #229954 100%)'
                          }}
                        >
                          {item.tracked ? '📥 ยอดยกมา' : '✅ เปิดใช้สต็อก'}
                        </button>
                        {item.tracked && (
                          <button
                            onClick={() => {
                              setSelectedItem(item);
                              setShowEditModal(true);
                            }}
                            className="button"
                            style={{
                              padding: '0.4rem 0.8rem',
                              fontSize: '0.85rem',
                              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)'
                            }}
                          >
                            ⚙️ ตั้งค่า
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredItems.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              ไม่พบข้อมูลสต๊อก
            </div>
          )}
        </div>
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <PurchaseModal
          menuItems={menuItems}
          onClose={() => setShowPurchaseModal(false)}
          onSubmit={handlePurchase}
          submitting={purchaseSubmitting}
          apiUrl={apiUrl}
        />
      )}

      {/* Edit Settings Modal */}
      {showEditModal && selectedItem && (
        <EditStockModal
          stockItem={selectedItem}
          onClose={() => setShowEditModal(false)}
          onSubmit={(settings) => handleUpdateSettings(selectedItem.menuItemId, settings)}
          submitting={settingsSubmitting}
        />
      )}

      {/* Issue Modal */}
      {showIssueModal && (
        <IssueModal
          stockItems={stockItems.filter(s => s.tracked)}
          onClose={() => setShowIssueModal(false)}
          onSubmit={handleIssue}
          submitting={issueSubmitting}
        />
      )}

      {/* Opening Balance Modal */}
      {showOpeningModal && selectedItem && (
        <OpeningBalanceModal
          stockItem={selectedItem}
          onClose={() => setShowOpeningModal(false)}
          onSubmit={(payload) => handleOpeningBalance(selectedItem.menuItemId, payload)}
          submitting={openingSubmitting}
        />
      )}
    </div>
  );
}

// Issue Modal — food used internally (soup base, staff meals, comps).
// Quantity is positive here; the server negates it.
function IssueModal({ stockItems, onClose, onSubmit, submitting }) {
  const PURPOSES = [
    { value: 'soup_base', label: 'เข้าน้ำซุป / เตรียมครัว' },
    { value: 'staff_meal', label: 'อาหารพนักงาน' },
    { value: 'comp', label: 'แถมลูกค้า' },
    { value: 'promo', label: 'โปรโมชั่น / ชิม' },
    { value: 'other', label: 'อื่นๆ' }
  ];

  const [formData, setFormData] = useState({
    menuItemId: '', quantity: '', purpose: '', notes: ''
  });

  const selected = stockItems.find(s => s.menuItemId === formData.menuItemId);
  const issueQty = parseFloat(formData.quantity);
  const current = selected?.currentQuantity ?? 0;
  const notEnough = Number.isFinite(issueQty) && issueQty > current;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    onSubmit({
      menuItemId: formData.menuItemId,
      quantity: parseFloat(formData.quantity),
      purpose: formData.purpose,
      notes: formData.notes
    });
  };

  return (
    <Modal open onClose={onClose} title="📤 เบิกใช้">
        <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1rem' }}>
          ของที่ใช้ในร้านโดยไม่ผ่านการขาย — ต้นทุนจะเข้ารายงานเป็นบรรทัดแยก
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>รายการ *</label>
            <select
              value={formData.menuItemId}
              onChange={(e) => setFormData({ ...formData, menuItemId: e.target.value })}
              required
            >
              <option value="">-- เลือกรายการ --</option>
              {stockItems.map(s => (
                <option key={s.menuItemId} value={s.menuItemId}>
                  {s.menuItem?.name || '-'} (คงเหลือ: {s.currentQuantity} {s.unit || ''})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>จำนวนที่เบิก *</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              placeholder="0.00"
              required
            />
            {notEnough && (
              <small style={{ color: '#f39c12', fontSize: '12px' }}>
                ของมีแค่ {current} {selected?.unit || ''} — ระบบจะหักเท่าที่มีและบันทึกส่วนที่ขาด
              </small>
            )}
          </div>

          <div className="form-group">
            <label>เบิกไปทำอะไร *</label>
            <select
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              required
            >
              <option value="">-- เลือกจุดประสงค์ --</option>
              {PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>หมายเหตุ</label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="รายละเอียดเพิ่มเติม"
            />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              className="button"
              style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
            >
              ยกเลิก
            </button>
            <button type="submit" className="button" disabled={submitting}>
              {submitting ? '⏳ กำลังบันทึก...' : '📤 บันทึก'}
            </button>
          </div>
        </form>
    </Modal>
  );
}

// Opening Balance Modal — sets an absolute starting quantity and cost.
// This is the audited alternative to hand-editing costPrice on the menu item,
// which the next purchase would silently overwrite.
function OpeningBalanceModal({ stockItem, onClose, onSubmit, submitting }) {
  const [formData, setFormData] = useState({
    quantity: stockItem.tracked ? String(stockItem.currentQuantity ?? 0) : '',
    unitCost: stockItem.tracked ? String(stockItem.averageCost ?? 0) : '',
    unit: stockItem.unit || stockItem.menuItem?.unit || 'กก.',
    minQuantity: stockItem.minQuantity === null ? '' : String(stockItem.minQuantity),
    trackingMode: stockItem.trackingMode || 'perpetual',
    quantityPerServing: String(stockItem.menuItem?.quantityPerServing ?? 1),
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    onSubmit({
      quantity: parseFloat(formData.quantity),
      unitCost: parseFloat(formData.unitCost),
      unit: formData.unit,
      trackingMode: formData.trackingMode,
      quantityPerServing: parseFloat(formData.quantityPerServing) || 1,
      ...(formData.minQuantity !== '' ? { minQuantity: parseFloat(formData.minQuantity) } : {}),
      notes: formData.notes
    });
  };

  return (
    <Modal open onClose={onClose} title={stockItem.tracked ? '📥 ตั้งยอดยกมา' : '✅ เปิดใช้สต็อก'}>
        <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1rem' }}>
          {stockItem.menuItem?.name || '-'}
        </p>

        <form onSubmit={handleSubmit}>
          {/* เลือกก่อนอย่างอื่น เพราะมันเปลี่ยนความหมายของตัวเลขทั้งหมดที่กรอกตามมา */}
          <div className="form-group">
            <label>ลูกค้าได้ของนี้ยังไง *</label>
            <select
              value={formData.trackingMode}
              onChange={(e) => setFormData({ ...formData, trackingMode: e.target.value })}
              required
            >
              <option value="perpetual">📱 สั่งผ่าน QR — ระบบหักให้ทุกออเดอร์</option>
              <option value="periodic">🧊 หยิบเองจากตู้ — รู้ยอดตอนเดินสต็อก</option>
            </select>
            <small style={{ color: '#aaa', fontSize: '12px' }}>
              {formData.trackingMode === 'periodic'
                ? 'ส่วนต่างตอนเดินสต็อกจะถูกนับเป็นต้นทุนอาหาร ไม่ใช่ของสูญเปล่า'
                : 'ส่วนต่างตอนเดินสต็อกจะถูกนับเป็นของหาย เพราะทุกการขายถูกหักไว้แล้ว'}
            </small>
          </div>

          <div className="form-group">
            <label>หน่วยนับ *</label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              required
            >
              {['กก.', 'กรัม', 'ชิ้น', 'ขวด', 'ลิตร', 'แก้ว', 'ถุง', 'แพ็ค'].map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>สั่ง 1 จาน หักกี่{formData.unit || 'หน่วย'} *</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.quantityPerServing}
              onChange={(e) => setFormData({ ...formData, quantityPerServing: e.target.value })}
              placeholder="1"
              required
            />
            <small style={{ color: '#aaa', fontSize: '12px' }}>
              เช่น ซื้อเป็นกิโล 1 จานได้ 1 ขีด ให้ใส่ 0.1 — ตั้งผิดจะหักผิดทุกออเดอร์
            </small>
          </div>

          <div className="form-group">
            <label>จำนวนที่มีอยู่จริงตอนนี้ *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              placeholder="0.00"
              required
            />
            <small style={{ color: '#aaa', fontSize: '12px' }}>
              ตั้งเป็นยอดจริง ไม่ใช่ยอดที่จะเพิ่ม — ระบบจะบันทึกส่วนต่างไว้ในประวัติ
            </small>
          </div>

          <div className="form-group">
            <label>ต้นทุนต่อ 1 {formData.unit} *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.unitCost}
              onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
              placeholder="0.00"
              required
            />
            <small style={{ color: '#aaa', fontSize: '12px' }}>
              ต้นทุนวัตถุดิบ ไม่ใช่ราคาขาย — การซื้อเข้าครั้งถัดไปจะถัวเฉลี่ยจากตัวเลขนี้
            </small>
          </div>

          <div className="form-group">
            <label>แจ้งเตือนเมื่อต่ำกว่า</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.minQuantity}
              onChange={(e) => setFormData({ ...formData, minQuantity: e.target.value })}
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label>หมายเหตุ</label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="เช่น นับสต็อกวันเปิดระบบ"
            />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              className="button"
              style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
            >
              ยกเลิก
            </button>
            <button type="submit" className="button" disabled={submitting}>
              {submitting ? '⏳ กำลังบันทึก...' : '💾 บันทึก'}
            </button>
          </div>
        </form>
    </Modal>
  );
}

// Purchase Modal Component
function PurchaseModal({ menuItems, onClose, onSubmit, submitting, apiUrl }) {
  const [formData, setFormData] = useState({
    menuItemId: '',
    quantity: '',
    unitCost: '',
    unit: 'กก.',
    supplier: '',
    invoiceNumber: '',
    notes: ''
  });
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState(null);

  const handleBarcodeScan = async (barcode) => {
    console.log('[PurchaseModal] Scanned barcode:', barcode);
    setScanError(null);
    setShowScanner(false); // Close scanner after successful scan

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/stock/barcode/${barcode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('ไม่พบสินค้าที่มีบาร์โค้ดนี้');
      }

      const result = await response.json();
      const data = result.data;

      // Auto-fill form with menu item data
      setFormData({
        ...formData,
        menuItemId: data.menuItemId,
        unit: data.unit || formData.unit
      });

      console.log('[PurchaseModal] Auto-filled:', data.name);
    } catch (error) {
      console.error('[PurchaseModal] Barcode lookup failed:', error);
      setScanError(error.message || 'ไม่พบสินค้า กรุณาเลือกจาก dropdown');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      quantity: parseFloat(formData.quantity),
      unitCost: parseFloat(formData.unitCost)
    });
  };

  const totalCost = (parseFloat(formData.quantity) || 0) * (parseFloat(formData.unitCost) || 0);

  return (
    <Modal open onClose={onClose} title="➕ บันทึกการซื้อเข้า">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ marginBottom: 0 }}>เมนู *</label>
              <button
                type="button"
                onClick={() => setShowScanner(!showScanner)}
                className="button"
                style={{
                  padding: '0.4rem 0.8rem',
                  fontSize: '0.85rem',
                  background: showScanner
                    ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                    : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)'
                }}
              >
                {showScanner ? '❌ ปิดกล้อง' : '📷 สแกนบาร์โค้ด'}
              </button>
            </div>

            {showScanner && (
              <div style={{ marginBottom: '1rem' }}>
                <BarcodeScanner
                  onScan={handleBarcodeScan}
                  onError={(err) => console.error('Scanner error:', err)}
                  isActive={showScanner}
                />
              </div>
            )}

            {scanError && (
              <div style={{
                padding: '0.75rem',
                background: 'rgba(231, 76, 60, 0.1)',
                border: '1px solid #e74c3c',
                borderRadius: '6px',
                color: '#e74c3c',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                ❌ {scanError}
              </div>
            )}

            <select
              value={formData.menuItemId}
              onChange={(e) => setFormData({ ...formData, menuItemId: e.target.value })}
              required
            >
              <option value="">-- เลือกเมนู --</option>
              {menuItems.map(item => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>จำนวน *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="10"
                  required
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>หน่วย *</label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                >
                  {['กก.', 'กรัม', 'ชิ้น', 'ขวด', 'ลิตร', 'แก้ว', 'ถุง', 'แพ็ค'].map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>ราคาต้นทุน/หน่วย (฿) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.unitCost}
              onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
              placeholder="300"
              required
            />
          </div>

          <div className="form-group">
            <label>ผู้จัดจำหน่าย</label>
            <input
              type="text"
              value={formData.supplier}
              onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              placeholder="เช่น ตลาดสด, บริษัท ABC"
            />
          </div>

          <div className="form-group">
            <label>เลขที่ใบเสร็จ</label>
            <input
              type="text"
              value={formData.invoiceNumber}
              onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
              placeholder="INV-001"
            />
          </div>

          <div className="form-group">
            <label>หมายเหตุ</label>
            <textarea
              rows="2"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="เพิ่มหมายเหตุ (ถ้ามี)"
            />
          </div>

          <div style={{
            padding: '1rem',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            <strong>ต้นทุนรวม: {totalCost.toFixed(2)} ฿</strong>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              className="button"
              style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
            >
              ❌ ยกเลิก
            </button>
            <button type="submit" className="button" disabled={submitting}>
              {submitting ? '⏳ กำลังบันทึก...' : '✅ บันทึก'}
            </button>
          </div>
        </form>
    </Modal>
  );
}

// Edit Stock Settings Modal
function EditStockModal({ stockItem, onClose, onSubmit, submitting }) {
  const [formData, setFormData] = useState({
    minQuantity: stockItem.minQuantity || 0,
    maxQuantity: stockItem.maxQuantity || '',
    unit: stockItem.unit || 'กก.',
    trackingMode: stockItem.trackingMode || 'perpetual',
    quantityPerServing: String(stockItem.menuItem?.quantityPerServing ?? 1)
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      minQuantity: parseFloat(formData.minQuantity),
      maxQuantity: formData.maxQuantity ? parseFloat(formData.maxQuantity) : null,
      unit: formData.unit,
      trackingMode: formData.trackingMode,
      quantityPerServing: parseFloat(formData.quantityPerServing) || 1
    });
  };

  return (
    <Modal open onClose={onClose} title={<>⚙️ ตั้งค่าสต๊อก: {stockItem.menuItem?.name}</>}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>สั่ง 1 จาน หักกี่{formData.unit || 'หน่วย'}</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.quantityPerServing}
              onChange={(e) => setFormData({ ...formData, quantityPerServing: e.target.value })}
            />
            <small style={{ color: '#aaa', fontSize: '12px' }}>
              ตัวแปลงระหว่างจานที่ลูกค้าสั่ง กับปริมาณที่หายจากตู้
            </small>
          </div>

          <div className="form-group">
            <label>ลูกค้าได้ของนี้ยังไง</label>
            <select
              value={formData.trackingMode}
              onChange={(e) => setFormData({ ...formData, trackingMode: e.target.value })}
            >
              <option value="perpetual">📱 สั่งผ่าน QR — ระบบหักให้ทุกออเดอร์</option>
              <option value="periodic">🧊 หยิบเองจากตู้ — รู้ยอดตอนเดินสต็อก</option>
            </select>
            <small style={{ color: '#aaa', fontSize: '12px' }}>
              เปลี่ยนค่านี้กระทบว่าส่วนต่างจากการเดินสต็อกจะถูกนับเป็นต้นทุนอาหารหรือของสูญเปล่า
            </small>
          </div>
          <div className="form-group">
            <label>จำนวนขั้นต่ำ (สำหรับแจ้งเตือน)</label>
            <input
              type="number"
              step="0.01"
              value={formData.minQuantity}
              onChange={(e) => setFormData({ ...formData, minQuantity: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>จำนวนสูงสุด (ถ้ามี)</label>
            <input
              type="number"
              step="0.01"
              value={formData.maxQuantity}
              onChange={(e) => setFormData({ ...formData, maxQuantity: e.target.value })}
              placeholder="ไม่จำกัด"
            />
          </div>

          <div className="form-group">
            <label>หน่วยนับ</label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            >
              {['กก.', 'กรัม', 'ชิ้น', 'ขวด', 'ลิตร', 'แก้ว', 'ถุง', 'แพ็ค'].map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              className="button"
              style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
            >
              ยกเลิก
            </button>
            <button type="submit" className="button" disabled={submitting}>
              {submitting ? '⏳ กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
